import {Phone, Calendar, Shield, Sparkles, Check, MapPin, Award, DollarSign, Car, Info} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import CanonicalTag from '@/components/CanonicalTag'
import EmbeddedQuoteForm from '@/components/EmbeddedQuoteForm'

const VehicleWrapCostSanDiegoPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    document.title = 'Vehicle Wrap Cost San Diego | How Much Does a Vehicle Wrap Cost?'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute('content', 'How much does a vehicle wrap cost in San Diego? Educational pricing guide covering vinyl wrap cost, Colored Paint Protection Film cost, Tesla wrap cost, Jeep wrap cost, truck wrap cost, and what affects pricing. Call 858-282-8119.')
    }

    const faqScript = document.createElement('script')
    faqScript.type = 'application/ld+json'
    faqScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Why are vehicle wraps expensive?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Vehicle wraps require premium materials, skilled labor, and a clean controlled environment. The cost reflects precision patterning or bulk installation as needed, proper surface preparation, trim removal, edge tucking, and the material itself. Cheap wraps use inferior films and skip critical prep steps, leading to early failure.'
          }
        },
        {
          '@type': 'Question',
          name: 'Is wrapping a car cheaper than painting it?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A quality full vehicle wrap is often comparable to or less expensive than a high-quality professional paint job, while offering the advantage of being removable. A premium paint job can cost $5,000–$15,000+, while a full vinyl wrap typically ranges from $2,500–$6,000+ depending on vehicle size and material.'
          }
        },
        {
          '@type': 'Question',
          name: 'How long does a vehicle wrap last?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A professionally installed vinyl wrap lasts 5–7 years with proper care. Colored Paint Protection Film lasts up to 10 years. Longevity depends on material quality, sun exposure, wash habits, and whether the vehicle is stored indoors.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can a vehicle wrap be removed?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Vinyl wraps and Colored Paint Protection Film are both removable. When properly installed and removed by professionals, they come off cleanly without damaging the factory paint underneath, making wraps ideal for leased vehicles.'
          }
        },
        {
          '@type': 'Question',
          name: 'Does a vehicle wrap protect paint?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Vinyl wrap offers light protection against UV exposure and surface abrasion. Colored Paint Protection Film (Colored PPF) provides significantly more protection, including self-healing against light scratches, rock chip resistance, and UV defense backed by a manufacturer film warranty.'
          }
        },
        {
          '@type': 'Question',
          name: 'Why does a white vehicle cost more to wrap black?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Dark films applied over light paint require perfect edge work and full coverage to avoid color bleed-through at edges, seams, and around trim. This demands more precise installation, more material, and often trim removal — all of which add to the labor cost.'
          }
        },
        {
          '@type': 'Question',
          name: 'Is Colored Paint Protection Film worth the cost?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'For most vehicle owners, yes. Colored PPF costs more than vinyl wrap but delivers genuine rock chip and scratch protection, self-healing technology, warranties up to 10 years, and better resale value by preserving factory paint. You get a color change and full protection in one installation.'
          }
        },
        {
          '@type': 'Question',
          name: 'How much does a Tesla wrap cost in San Diego?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A full vinyl color change wrap for a Tesla Model Y or Model 3 typically ranges from $2,500–$4,500 depending on material and coverage. Colored Paint Protection Film for a full Tesla starts at $5,999. Final pricing depends on vehicle condition, coverage area, and material selection. Contact Mr Wrap for a personalized quote.'
          }
        },
        {
          '@type': 'Question',
          name: 'How much does a Jeep wrap cost in San Diego?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A Jeep Wrangler vinyl wrap typically ranges from $3,000–$5,500 due to the complex body panels, flares, and doors. Colored PPF for a Jeep runs higher due to the same complexity. Pricing is always determined after a vehicle inspection and consultation.'
          }
        }
      ]
    })
    document.head.appendChild(faqScript)

    const serviceScript = document.createElement('script')
    serviceScript.type = 'application/ld+json'
    serviceScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Vehicle Wrap and Paint Protection Film Installation',
      name: 'Vehicle Wrap Cost Guide — San Diego PPF Landing Page',
      description: 'Educational vehicle wrap pricing guide for San Diego. Covers vinyl wrap cost, Colored PPF cost, Tesla wrap cost, Jeep wrap cost, truck wrap cost, and the factors that affect vehicle wrap pricing.',
      areaServed: ['San Diego', 'Vista', 'Oceanside', 'Carlsbad', 'San Marcos', 'Encinitas', 'North County San Diego'],
      provider: {
        '@type': 'LocalBusiness',
        name: 'Mr Wrap - AutoWrapSanDiego',
        telephone: '+1-858-282-8119',
        priceRange: '$$$',
        url: 'https://autowrapsandiego.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2244 South Santa Fe Avenue, Suite A7',
          addressLocality: 'Vista',
          addressRegion: 'CA',
          postalCode: '92084',
          addressCountry: 'US'
        },
        areaServed: 'San Diego County'
      }
    })
    document.head.appendChild(serviceScript)

    const breadcrumbScript = document.createElement('script')
    breadcrumbScript.type = 'application/ld+json'
    breadcrumbScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://autowrapsandiego.com/' },
        { '@type': 'ListItem', position: 2, name: 'Car Wrap San Diego', item: 'https://autowrapsandiego.com/car-wrap-san-diego' },
        { '@type': 'ListItem', position: 3, name: 'Vehicle Wrap Cost San Diego', item: 'https://autowrapsandiego.com/vehicle-wrap-cost-san-diego' }
      ]
    })
    document.head.appendChild(breadcrumbScript)

    return () => {
      document.head.removeChild(faqScript)
      document.head.removeChild(serviceScript)
      document.head.removeChild(breadcrumbScript)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/vehicle-wrap-cost-san-diego" />
      <div className="pt-20 min-h-screen bg-black">

        {/* Hero */}
        <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
                How Much Does a Vehicle Wrap Cost in San Diego?
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
                Vehicle wrap pricing varies based on vehicle size, film type, installation complexity, and your goals. This guide covers realistic price ranges, the factors that affect cost, and the difference between vinyl wrap and Colored Paint Protection Film. For service details, visit our <a href="/car-wrap-san-diego" className="text-cyan-400 hover:underline">San Diego car wrap</a> page.
              </p>
              <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-8">
                Serving Vista, Oceanside, Carlsbad, San Marcos, Encinitas, Poway, La Jolla, Del Mar, and all of San Diego County.
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                  <Phone size={18} />
                  858-282-8119
                </a>
                <button onClick={() => { navigate('/contact'); setTimeout(() => window.scrollTo(0, 0), 100) }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                  <Calendar size={18} />
                  Get a Free Quote
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="bg-black border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <ol className="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
              <li><a href="/" className="hover:text-cyan-400 transition">Home</a></li>
              <li className="text-gray-600">›</li>
              <li><a href="/car-wrap-san-diego" className="hover:text-cyan-400 transition">Car Wrap San Diego</a></li>
              <li className="text-gray-600">›</li>
              <li className="text-cyan-400 font-medium" aria-current="page">Vehicle Wrap Cost San Diego</li>
            </ol>
          </div>
        </nav>

        {/* Trust Strip */}
        <section className="bg-gray-950 border-b border-cyan-500/20 py-5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                  <Award className="text-cyan-400" size={22} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">13 Years of Experience</p>
                  <p className="text-gray-400 text-xs">Professional wraps & PPF</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                  <Shield className="text-cyan-400" size={22} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Warranty-Backed Films</p>
                  <p className="text-gray-400 text-xs">Premium materials only</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                  <MapPin className="text-cyan-400" size={22} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Vista / North County SD</p>
                  <p className="text-gray-400 text-xs">Serving all San Diego County</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                  <Sparkles className="text-cyan-400" size={22} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Tesla · Corvette · Jeep</p>
                  <p className="text-gray-400 text-xs">Trucks, luxury & exotic builds</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Summary */}
        <section className="py-16 bg-gray-900 border-y border-cyan-500/20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-3 text-white text-center">Vehicle Wrap Pricing — Quick Summary</h2>
            <p className="text-gray-400 text-center mb-10 max-w-3xl mx-auto">Every vehicle wrap project is unique. Here is what every San Diego vehicle owner should understand about wrap pricing before requesting a quote.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">Pricing varies significantly:</strong> Vehicle size, shape, material type, and installation complexity all drive the final number. No two projects are identical.</p>
              </div>
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">Vehicle size matters:</strong> Sedans, trucks, SUVs, and Jeeps all require different amounts of material and labor — pricing reflects the actual surface area and complexity.</p>
              </div>
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">Material selection is a major cost factor:</strong> Standard vinyl, premium cast vinyl, color-shift films, and Colored Paint Protection Film all carry different price points.</p>
              </div>
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">Colored PPF costs more than vinyl:</strong> It delivers genuine rock chip protection, self-healing, and a manufacturer-backed film warranty — not just a cosmetic color change.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Vehicle Wrap Pricing Varies */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-6 text-white text-center">Why Vehicle Wrap Pricing Varies So Much</h2>
            <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">Understanding what drives wrap cost helps you make a better decision and evaluate quotes accurately. Here are the primary factors that affect vehicle wrap pricing in San Diego.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {[
                { title: 'Vehicle Size', body: 'A compact sedan requires far less material than a full-size truck or SUV. Larger vehicles take more film, more labor hours, and more time in the installation bay.' },
                { title: 'Vehicle Shape & Body Complexity', body: 'Vehicles with complex curves, deep recesses, wide door handles, or large flat panels like box trucks require more precise cutting and installation skill. A Jeep Wrangler with fender flares takes considerably more time than a Tesla Model 3.' },
                { title: 'Full Color Change vs Partial Wrap', body: 'A full color change wrap covering every painted surface costs more than a partial wrap limited to a roof, hood, or accent panels. Each additional panel adds material and labor.' },
                { title: 'Film Type: Satin, Matte, Gloss, or Color-Shift', body: 'Standard gloss films are the most accessible. Satin and matte films require more care during installation and often cost more. Color-shift and specialty metallic films carry a significant premium.' },
                { title: 'Vinyl Wrap vs Colored Paint Protection Film', body: 'Vinyl wrap is a cosmetic film. Colored PPF (Colored Paint Protection Film) is a protective film that also changes color — it is thicker, more labor-intensive, and priced accordingly.' },
                { title: 'Existing Wrap Removal', body: 'If your vehicle has an existing wrap that needs to be removed before the new installation, that labor adds to the project cost. Adhesive residue cleanup can add hours to the prep process.' },
                { title: 'Paint Condition', body: 'Paint that is chipped, oxidized, or has previous body work may need correction before wrap installation. Wrapping over poor paint shortens film life and can telegraph imperfections.' },
                { title: 'Body Damage or Dents', body: 'Significant body damage should be addressed before wrapping. Installing film over dents or creases results in a subpar finish and can cause premature failure at those points.' },
                { title: 'Chrome Delete & Trim Work', body: 'Adding chrome delete, badge blackout, or trim wraps alongside a full color change increases material usage and installation time — but creates a much cleaner, cohesive final result.' },
                { title: 'Roof Wrap & Mirror Wraps', body: 'Roof wraps and mirror wraps are popular add-ons that require individual attention. Panoramic glass roofs and complex mirror shapes add to install time.' },
                { title: 'Trim Removal & Disassembly', body: 'A quality wrap installer removes trim pieces, door handles, and sometimes bumpers to wrap edges properly. This disassembly and reassembly adds labor but produces far superior results that last longer.' },
                { title: 'Installation Complexity & Wrap Quality', body: 'Edge tucking, door jamb wrapping, and proper heat stretching around curves all take skill and time. Shops that skip these steps produce cheaper-looking results that fail earlier.' }
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                  <div className="flex items-start gap-3">
                    <Info className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h3 className="text-white font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Typical Price Ranges */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">Typical Vehicle Wrap Price Ranges in San Diego</h2>
            <p className="text-gray-400 text-center mb-4 max-w-3xl mx-auto">These are educational ranges based on typical projects. Your final price depends on your vehicle's specific condition, the material you select, the coverage area, and installation requirements. Contact us for a personalized quote.</p>
            <div className="flex items-center justify-center gap-2 mb-12">
              <DollarSign className="text-orange-400" size={18} />
              <p className="text-orange-300 text-sm font-medium">Disclaimer: Ranges shown are starting reference points only. Exact pricing is determined after a free consultation and vehicle inspection.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { vehicle: 'Compact Sedan', example: 'Tesla Model 3, Honda Civic, Toyota Camry', vinylRange: '$2,500 – $4,000', ppfRange: 'Starting at $5,999', notes: 'Sedans have relatively manageable surface area but details like door edges and trunk lip require precision.' },
                { vehicle: 'Tesla Model Y', example: 'SUV crossover — popular San Diego wrap', vinylRange: '$2,800 – $4,500', ppfRange: 'Starting at $5,999', notes: 'Tesla soft factory paint makes protection especially important. Stealth and satin finishes are the most requested.' },
                { vehicle: 'Midsize & Full-Size SUV', example: 'Ford Explorer, Chevy Tahoe, BMW X5', vinylRange: '$3,500 – $5,500', ppfRange: 'Starting at $5,999', notes: 'More surface area than sedans. Larger panels may require more film and more installation time, which can affect final pricing.' },
                { vehicle: 'Truck (Full Size)', example: 'Ford F-150, RAM 1500, Chevy Silverado', vinylRange: '$3,000 – $5,000', ppfRange: 'Starting at $5,999', notes: 'Full-size truck beds and tailgates add complexity. Bed liner considerations affect scope.' },
                { vehicle: 'Jeep Wrangler', example: 'JL, JK, and Gladiator models', vinylRange: '$3,000 – $5,500', ppfRange: 'Starting at $5,999', notes: 'Complex body — fender flares, multiple doors, and exposed hardware make Jeep wraps more labor-intensive, and larger/complex vehicles may cost more.' },
                { vehicle: 'Commercial Vehicle', example: 'Vans, box trucks, service vehicles', vinylRange: 'Custom quote', ppfRange: 'N/A — PPF for branding inquiries', notes: 'Commercial wraps are priced per vehicle based on coverage, branding complexity, and fleet size.' }
              ].map((row, i) => (
                <div key={i} className="bg-black p-6 rounded-xl border border-cyan-500/20">
                  <h3 className="text-white font-bold text-lg mb-1">{row.vehicle}</h3>
                  <p className="text-gray-500 text-xs mb-4 italic">{row.example}</p>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Vinyl Wrap</span>
                      <span className="text-cyan-400 font-semibold text-sm">{row.vinylRange}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">Colored PPF</span>
                      <span className="text-orange-400 font-semibold text-sm">{row.ppfRange}</span>
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed border-t border-gray-800 pt-3">{row.notes}</p>
                </div>
              ))}
            </div>
            <div className="bg-gray-800 rounded-xl p-6 mt-10 max-w-4xl mx-auto border border-cyan-500/20">
              <p className="text-gray-300 text-sm text-center leading-relaxed">
                <strong className="text-white">Important:</strong> All ranges above are educational starting points. Final pricing depends on your vehicle's exact condition, paint quality, material selected, coverage scope, and installation requirements. Every project is evaluated individually. Call <a href="tel:858-282-8119" className="text-cyan-400 hover:underline">858-282-8119</a> or <button onClick={() => { navigate('/contact'); setTimeout(() => window.scrollTo(0, 0), 100) }} className="text-orange-400 hover:underline">request a quote online</button> for an accurate estimate.
              </p>
            </div>
          </div>
        </section>

        {/* Mid-page CTA */}
        <section className="py-14 bg-gradient-to-r from-cyan-600 to-cyan-500">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4 text-white">Every Project Is Unique — Get a Personalized Quote</h2>
            <p className="text-lg text-white/90 mb-6">Our Vista team will assess your vehicle, discuss your goals, and provide an honest, no-pressure price estimate.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-3 px-10 rounded-lg transition inline-flex items-center justify-center gap-2">
                <Phone size={20} />
                Call 858-282-8119
              </a>
              <button onClick={() => { navigate('/contact'); setTimeout(() => window.scrollTo(0, 0), 100) }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-10 rounded-lg transition inline-flex items-center justify-center gap-2">
                <Calendar size={20} />
                Get Free Quote
              </button>
            </div>
          </div>
        </section>

        {/* Vinyl Wrap vs Colored PPF Comparison */}
        <section className="py-20 bg-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">Vinyl Wrap vs Colored Paint Protection Film — Cost Comparison</h2>
            <p className="text-gray-400 text-center mb-4 max-w-3xl mx-auto">Understanding the difference between vinyl wrap and Colored Paint Protection Film is essential before deciding which solution is right for your vehicle and budget.</p>
            <p className="text-gray-300 text-center mb-10 max-w-3xl mx-auto">
              Vinyl wrap is an excellent option for color change on a budget. <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:underline">Colored Paint Protection Film</a> is the premium solution for customers who want both a color change and genuine paint protection in one installation.
            </p>
            <div className="overflow-x-auto rounded-xl border border-cyan-500/20 mb-8">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-900">
                    <th className="p-4 text-white font-bold border-b border-cyan-500/20">Feature</th>
                    <th className="p-4 text-cyan-400 font-bold border-b border-cyan-500/20">Colored Paint Protection Film</th>
                    <th className="p-4 text-gray-300 font-bold border-b border-cyan-500/20">Vinyl Wrap</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="bg-black">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Typical Investment</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Higher — protection included</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Lower — cosmetic only</td>
                  </tr>
                  <tr className="bg-gray-900/50">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Paint Protection</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Full — rock chips, scratches, UV</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Minimal / cosmetic only</td>
                  </tr>
                  <tr className="bg-black">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Thickness</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Thick urethane (~8 mil)</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Thin cosmetic film (~2–3.5 mil)</td>
                  </tr>
                  <tr className="bg-gray-900/50">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Longevity</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Up to 10 years</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">5–7 years</td>
                  </tr>
                  <tr className="bg-black">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Self-Healing</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Yes — heals with heat</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">No</td>
                  </tr>
                  <tr className="bg-gray-900/50">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Maintenance</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Easy — hydrophobic topcoat</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Moderate — more delicate</td>
                  </tr>
                  <tr className="bg-black">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Appearance</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Gloss, satin, matte, stealth</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Widest range of colors & finishes</td>
                  </tr>
                  <tr className="bg-gray-900/50">
                    <td className="p-4 text-white font-semibold">Best Use Case</td>
                    <td className="p-4 text-gray-300">Color change + lasting protection</td>
                    <td className="p-4 text-gray-400">Budget or short-term color change</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-center text-gray-400">
              Learn more: <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:underline">Colored PPF San Diego</a> · <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">Paint Protection Film San Diego</a> · <a href="/vinyl-wraps" className="text-cyan-400 hover:underline">Vinyl Wrap Services</a>
            </p>
          </div>
        </section>

        {/* Why Some Shops Charge More */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">Why Some Vehicle Wrap Shops Charge More</h2>
            <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">Not all vehicle wraps are created equal. The price difference between shops often reflects real, meaningful differences in quality — not just margin. Here is what separates premium installations from budget work.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Shield, title: 'Premium, Warranty-Backed Films', body: 'Top-tier shops use certified materials from manufacturers like Avery Dennison and 3M. Budget shops use lower-grade films that fade, shrink, and fail within a few years.' },
                { icon: Award, title: 'Installer Experience', body: 'Experienced installers produce cleaner edges, better heat stretching around curves, and installations that last. Experience takes years to develop.' },
                { icon: Car, title: 'Proper Surface Preparation', body: 'A quality installation begins with thorough decontamination, clay barring, and panel wiping. Rushing prep creates adhesion failures and visible texture under the film.' },
                { icon: Sparkles, title: 'Full Disassembly & Edge Work', body: 'Removing trim, door handles, and badges to wrap edges properly takes additional time but produces a clean, seamless, factory-quality result that lasts far longer.' },
                { icon: Check, title: 'Wrapped Edges & Door Jambs', body: 'Tucking film inside door jambs and wrapping around panel edges prevents lifting, peeling, and moisture intrusion that causes early failure.' },
                { icon: DollarSign, title: 'Labor-Intensive Vehicles', body: 'Complex vehicles like Jeep Wranglers, Corvettes, and exotic cars require significantly more time due to their body geometry. That labor investment shows in the result.' },
                { icon: Info, title: 'Design Quality', body: 'For commercial and custom graphic wraps, professional design that accounts for panel breaks, curves, and print registration produces a result that looks intentional rather than generic.' },
                { icon: MapPin, title: 'Controlled Installation Environment', body: 'A clean, controlled indoor installation bay supports clean, bubble-free results. Film applied in dirty or cold environments rarely performs well long-term.' }
              ].map((item, i) => (
                <div key={i} className="bg-black p-6 rounded-xl border border-cyan-500/20">
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 w-fit mb-4">
                    <item.icon className="text-cyan-400" size={22} />
                  </div>
                  <h3 className="text-white font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Popular Vehicles We Wrap */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">Popular Vehicles We Wrap in San Diego</h2>
            <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">From Teslas and sports cars to Jeeps and trucks, here is what San Diego vehicle owners are wrapping most — and what affects pricing for each.</p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-3 text-white">Tesla Model Y &amp; Model 3 Wrap Cost</h3>
                <p className="text-gray-300">Tesla wraps are among our most-requested services. Tesla's soft factory paint is highly susceptible to rock chips on San Diego freeways, making both vinyl wrap and <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:underline">Colored Paint Protection Film</a> popular upgrades. Stealth black and satin gray are the most requested finishes. See our dedicated <a href="/tesla-wrap-san-diego" className="text-cyan-400 hover:underline">Tesla wrap San Diego</a> and <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla PPF San Diego</a> pages for more detail.</p>
              </div>
              <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-3 text-white">Corvette Wrap Cost</h3>
                <p className="text-gray-300">C8 Corvettes and older generations are prime wrap candidates. The low front splitter, wide rocker panels, and complex body lines require skilled installers. Corvette owners often choose aggressive stealth, satin, or custom metallic finishes that transform the look while protecting the paint on a vehicle worth protecting.</p>
              </div>
              <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-3 text-white">Jeep Wrangler Wrap Cost</h3>
                <p className="text-gray-300">Jeep Wranglers are among the most labor-intensive vehicles to wrap due to their fender flares, multiple doors, and exposed hardware. Matte military green, flat black, and custom patterns are popular. For off-road Jeeps, <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:underline">Colored Paint Protection Film</a> provides real protection against trail debris alongside the color change.</p>
              </div>
              <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-3 text-white">Truck Wrap Cost</h3>
                <p className="text-gray-300">Full-size trucks — F-150, Silverado, RAM, Tacoma — are popular wrap candidates in San Diego. Matte black, satin gray, and two-tone builds are common requests. Truck beds and tailgates add material and time to the project scope. Many truck owners add chrome delete and <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">window tinting</a> for a complete build.</p>
              </div>
              <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-3 text-white">SUV Wrap Cost</h3>
                <p className="text-gray-300">Midsize and full-size SUVs — BMW X5, Range Rover, Mercedes GLE, Tahoe — have more surface area than sedans and require more material and labor. SUV owners often opt for full color change wraps that transform the entire vehicle, sometimes paired with <a href="/chrome-delete-san-diego" className="text-cyan-400 hover:underline">chrome delete</a> for a blacked-out look.</p>
              </div>
              <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-3 text-white">Commercial Vehicle Wrap Cost</h3>
                <p className="text-gray-300">Commercial vehicle wraps — vans, box trucks, service trucks — are priced based on coverage area, branding complexity, and fleet size. Volume discounts apply for multi-vehicle projects. See our <a href="/commercial-fleet-wraps" className="text-cyan-400 hover:underline">commercial fleet wraps</a> page for commercial pricing details.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">Vehicle Wrap Cost FAQs — San Diego</h2>
            <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">Common questions about vehicle wrap pricing, materials, and what to expect.</p>
            <div className="space-y-5">
              {[
                {
                  q: 'Why are vehicle wraps expensive?',
                  a: 'Vehicle wraps require premium materials, skilled labor, and a clean controlled environment. The cost reflects precision patterning or bulk installation as needed, proper surface preparation, trim removal, edge tucking, and the material itself. Cheap wraps use inferior films and skip critical prep steps, leading to early failure.'
                },
                {
                  q: 'Is wrapping a car cheaper than painting it?',
                  a: 'A quality full vehicle wrap is often comparable to or less expensive than a high-quality professional paint job, while offering the advantage of being removable. A premium paint job can cost $5,000–$15,000+, while a full vinyl wrap typically ranges from $2,500–$6,000+ depending on vehicle size and material.'
                },
                {
                  q: 'How long does a vehicle wrap last in San Diego?',
                  a: 'A professionally installed vinyl wrap lasts 5–7 years with proper care in San Diego\'s climate. Colored Paint Protection Film lasts up to 10 years. Longevity depends on material quality, sun exposure, wash habits, and indoor vs outdoor storage.'
                },
                {
                  q: 'Can a vehicle wrap be removed?',
                  a: 'Yes. Vinyl wraps and Colored Paint Protection Film are both removable. When properly installed and removed by professionals, they come off cleanly without damaging the factory paint, making wraps ideal for leased vehicles.'
                },
                {
                  q: 'Does a vehicle wrap protect paint?',
                  a: 'Vinyl wrap offers light protection against UV exposure and surface abrasion. Colored Paint Protection Film provides significantly more protection — including self-healing against light scratches, rock chip resistance, and UV defense backed by a manufacturer film warranty.'
                },
                {
                  q: 'Why does wrapping a white car black cost more?',
                  a: 'Dark films over light paint require perfect edge work and full coverage to avoid color bleed-through at edges, seams, and trim. This demands more precise installation, more material, and often trim removal — all of which add to the labor cost.'
                },
                {
                  q: 'Is Colored Paint Protection Film worth the cost?',
                  a: 'For most owners, yes. Colored PPF costs more than vinyl wrap but delivers genuine rock chip and scratch protection, self-healing technology, warranties up to 10 years, and better resale value by preserving factory paint. You get a color change and full protection in one installation — instead of two separate services.'
                },
                {
                  q: 'How much does a Tesla wrap cost in San Diego?',
                  a: 'A full vinyl color change wrap for a Tesla Model Y or Model 3 typically ranges from $2,500–$4,500 depending on material and coverage. Colored Paint Protection Film for a full Tesla starts at $5,999. Final pricing depends on vehicle condition, coverage area, and material selection. Contact us for a personalized quote.'
                },
                {
                  q: 'How much does a Jeep wrap cost in San Diego?',
                  a: 'A Jeep Wrangler vinyl wrap typically ranges from $3,000–$5,500 due to the complex body panels, fender flares, and doors. Colored PPF for a Jeep runs higher due to the same complexity. Pricing is always determined after a vehicle inspection and consultation.'
                }
              ].map((item, i) => (
                <div key={i} className="bg-black p-6 rounded-xl border border-cyan-500/20">
                  <h3 className="text-xl font-bold mb-3 text-white">{item.q}</h3>
                  <p className="text-gray-300 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Mr Wrap */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Mr Wrap in Vista, North County San Diego</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                { icon: Award, title: '13 Years of Experience', body: '13 years of professional wrap and PPF installation for San Diego vehicles. Experience shows in the finish.' },
                { icon: MapPin, title: 'Vista / North County SD Location', body: 'Conveniently located at 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084 — easy access from Oceanside, Carlsbad, San Marcos, Encinitas, and greater San Diego.' },
                { icon: Shield, title: 'Premium, Warranty-Backed Materials', body: 'We use only top-tier certified wrap films and PPF products. No budget materials, no shortcuts — every install uses materials worth backing with a warranty.' },
                { icon: Sparkles, title: 'Tesla, Corvette, Jeep & Luxury Experience', body: 'From Tesla\'s soft paint to Corvette\'s complex body lines and Jeep\'s fender flares, we have specific experience with the vehicles San Diego drivers care about.' },
                { icon: Car, title: 'Truck, SUV & Exotic Builds', body: 'Full-size trucks, luxury SUVs, and exotic supercars all receive the same precision approach — proper prep, trim removal, and flawless installation every time.' },
                { icon: Check, title: 'Quality-First Installation Process', body: 'Every vehicle goes through full decontamination, precision patterning or bulk installation as needed, edge tucking, and detailed quality inspection before we consider the job done.' }
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                  <div className="bg-cyan-500/20 w-14 h-14 rounded-full flex items-center justify-center mb-5 border border-cyan-500">
                    <item.icon className="text-cyan-400" size={28} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Area */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
              <MapPin className="text-cyan-400" size={30} />
              Vehicle Wrap Cost Guide — Serving San Diego County
            </h2>
            <p className="text-gray-300 text-lg mb-8 text-center">
              Based in Vista, we install vehicle wraps and Colored Paint Protection Film throughout San Diego County and North County.
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-sm">
              <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
                <h3 className="font-bold text-white mb-2">North County Coastal</h3>
                <ul className="space-y-1">
                  <li>• Oceanside</li>
                  <li>• Carlsbad</li>
                  <li>• Encinitas</li>
                  <li>• Solana Beach</li>
                  <li>• Del Mar</li>
                </ul>
              </div>
              <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
                <h3 className="font-bold text-white mb-2">North County Inland</h3>
                <ul className="space-y-1">
                  <li>• Vista (Shop Location)</li>
                  <li>• San Marcos</li>
                  <li>• Escondido</li>
                  <li>• Rancho Santa Fe</li>
                  <li>• Poway</li>
                </ul>
              </div>
              <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
                <h3 className="font-bold text-white mb-2">Greater San Diego</h3>
                <ul className="space-y-1">
                  <li>• San Diego</li>
                  <li>• La Jolla</li>
                  <li>• Mission Valley</li>
                  <li>• Downtown SD</li>
                  <li>• All SD County</li>
                </ul>
              </div>
            </div>
            <p className="text-gray-300 text-center mt-8">
              <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
            </p>
          </div>
        </section>

        {/* Embedded Quote Form */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <EmbeddedQuoteForm
              defaultService="vehicle-wrap"
              heading="Get a Personalized Vehicle Wrap Quote"
              subheading="Every project is unique. Tell us about your vehicle and we'll provide an honest, no-pressure estimate — not a generic price sheet."
            />
          </div>
        </section>

        {/* Related Services */}
        <section className="py-16 bg-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { href: '/colored-ppf-san-diego', title: 'Colored PPF San Diego', desc: 'Color change paint protection film — the premium alternative to vinyl wrap' },
                { href: '/car-wrap-san-diego', title: 'Car Wrap San Diego', desc: 'Full vinyl color change wraps and custom wrap installations' },
                { href: '/paint-protection-film-san-diego', title: 'Paint Protection Film San Diego', desc: 'Clear self-healing PPF for ultimate rock chip and UV protection' },
                { href: '/tesla-wrap-san-diego', title: 'Tesla Wrap San Diego', desc: 'Dedicated Tesla wrap services — stealth, satin, and color change builds' },
                { href: '/ceramic-coating-san-diego', title: 'Ceramic Coating San Diego', desc: 'Hydrophobic, UV-resistant ceramic coating for paint, wraps, and PPF' },
                { href: '/commercial-fleet-wraps', title: 'Commercial Fleet Wraps', desc: 'Commercial vehicle and fleet graphics for San Diego businesses' }
              ].map((item, i) => (
                <a key={i} href={item.href} className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6 text-white">Ready for a Personalized Vehicle Wrap Quote?</h2>
            <p className="text-xl text-white/90 mb-4">
              Every project is unique. The only way to get an accurate price is to discuss your specific vehicle, goals, and material preferences with our team.
            </p>
            <p className="text-lg text-white/80 mb-8">
              Call <strong>858-282-8119</strong> or request a quote online — no pressure, no obligation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => { navigate('/contact'); setTimeout(() => window.scrollTo(0, 0), 100) }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
                <Calendar size={20} />
                Get Free Quote
              </button>
              <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
                <Phone size={20} />
                Call: 858-282-8119
              </a>
            </div>
          </div>
        </section>

      </div>
    </>
  )
}

export default VehicleWrapCostSanDiegoPage
